'use client';

import React, { useEffect, useRef } from 'react';
import 'katex/dist/katex.min.css';
import katex from 'katex';

interface QuillContentRendererProps {
  content: string;
  className?: string;
}

export const QuillContentRenderer: React.FC<QuillContentRendererProps> = ({
  content,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !content) return;

    // Render the QuillJS content
    const container = containerRef.current;
    
    // Parse the QuillJS delta format and render it
    try {
      // If content is HTML (from QuillJS), render it directly
      if (content.includes('<')) {
        container.innerHTML = content;
      } else {
        // If it's plain text, wrap it in a paragraph
        container.innerHTML = `<p>${content}</p>`;
      }

      // Find and render math equations
      const mathElements = container.querySelectorAll('.ql-formula');
      mathElements.forEach((element) => {
        const latex = element.getAttribute('data-value');
        if (latex) {
          try {
            katex.render(latex, element as HTMLElement, {
              throwOnError: false,
              displayMode: false
            });
          } catch (error) {
            console.error('Error rendering math equation:', error);
            element.textContent = latex; // Fallback to raw LaTeX
          }
        }
      });

      // Also look for any LaTeX content that might not be wrapped in .ql-formula
      const textContent = container.textContent || '';
      const latexRegex = /\$\$([^$]+)\$\$|\$([^$]+)\$/g;
      let match;
      let newContent = content;

      while ((match = latexRegex.exec(textContent)) !== null) {
        const fullMatch = match[0];
        const latex = match[1] || match[2];
        const isDisplayMode = fullMatch.startsWith('$$');
        
        try {
          const rendered = katex.renderToString(latex, {
            throwOnError: false,
            displayMode: isDisplayMode
          });
          newContent = newContent.replace(fullMatch, rendered);
        } catch (error) {
          console.error('Error rendering LaTeX:', error);
        }
      }

      if (newContent !== content) {
        container.innerHTML = newContent;
      }

    } catch (error) {
      console.error('Error rendering QuillJS content:', error);
      container.innerHTML = `<p>${content}</p>`;
    }
  }, [content]);

  return (
    <div 
      ref={containerRef}
      className={`quill-content-renderer ${className}`}
      style={{
        lineHeight: '1.6',
        fontSize: '14px'
      }}
    />
  );
}; 